'use client';

import {
	BN,
	BigNum,
	UnifiedSwapClient,
	SwapQuote,
	SwapMode,
	SpotMarketConfig,
	VelocityClient,
	TxParams,
} from '@velocity-exchange/sdk';
import {
	AddressLookupTableAccount,
	PublicKey,
	VersionedTransaction,
	RpcResponseAndContext,
	SimulatedTransactionResponse,
	ParsedInstruction,
	SimulateTransactionConfig,
	TransactionInstruction,
} from '@solana/web3.js';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import {
	DEFAULT_SWAP_MAX_ACCOUNTS,
	MIN_SWAP_MAX_ACCOUNTS,
	SWAP_MAX_ACCOUNTS_RETRY_STEP,
} from 'src/constants/swap';
import { SlippageTolerance } from 'src/@types/types';
import { GetTxParamsProps } from '../../transactions/useGetTxParams';
import {
	createSwapClient,
	getQuoteSwapClient,
	isValidQuote,
	swapOutputForcesJupiter,
} from 'src/utils/swap';
import {
	MAX_TX_BYTE_SIZE,
	getSerializedTransactionSize,
} from 'src/utils/transaction';
import { FEE_PAYER_WALLET } from 'src/utils/wallets/feePayerWallet';

/** Backoff between retries, so a failing swap doesn't spam the RPC with 429s. */
const RETRY_BASE_DELAY_MS = 250;
const RETRY_MAX_DELAY_MS = 2_000;

const getRetryDelayMs = (retries: number) => {
	const backoff = Math.min(
		RETRY_BASE_DELAY_MS * 2 ** retries,
		RETRY_MAX_DELAY_MS
	);
	// jitter so concurrent clients don't retry in lockstep
	return backoff / 2 + Math.random() * (backoff / 2);
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Error from a failed attempt, carrying the simulation logs when there are any
 * so downstream handlers can match on-chain program errors.
 */
export type SwapAttemptError = Error & { logs?: string[] };

const toSwapAttemptError = (e: unknown, logs?: string[]): SwapAttemptError => {
	const error: SwapAttemptError =
		e instanceof Error ? e : new Error(String(e ?? 'Unknown swap error'));

	if (logs?.length) {
		error.logs = logs;
	}

	return error;
};

type BuildSwapTxWithRetryLogicBaseProps = {
	accountPubKey: PublicKey;
	inputMarketConfig: SpotMarketConfig;
	outputMarketConfig: SpotMarketConfig;
	inAssociatedTokenAccount?: PublicKey;
	outAssociatedTokenAccount?: PublicKey;
	amount: BN;
	swapMode: SwapMode;
	slippage: SlippageTolerance;
	usdEstimate: number;
	maxRetries: number;
	onlyDirectRoutes?: boolean;
	additionalIxs?: TransactionInstruction[];
	forceJupiterClient?: boolean;
	velocityClient: VelocityClient;
	swapClient: UnifiedSwapClient;
	getTxParams: (props?: GetTxParamsProps) => TxParams;
	/**
	 * Quote the user already approved. Used for the first attempt only —
	 * retries re-quote, since a stale quote would just fail the same way.
	 */
	quoteToUse?: SwapQuote;
	/**
	 * Route account budget. Must be supplied by callers that computed one for
	 * their display quote, otherwise the tx is built against a wider route than
	 * the one the user was quoted.
	 */
	maxAccounts?: number;
	/**
	 * Build — and therefore simulate and size-check — with the sponsor as payer,
	 * matching the transaction signAndSendTransaction sends for fee-payer
	 * wallets. Without it a 0-SOL embedded wallet fails simulation as payer, and
	 * the size check under-measures by the sponsor's extra key + signature.
	 */
	useFeePayer?: boolean;
};

export type BuildSwapTxWithRetryLogicProps = BuildSwapTxWithRetryLogicBaseProps;

export const buildSwapTxWithRetryLogic = async ({
	accountPubKey,
	inputMarketConfig,
	outputMarketConfig,
	inAssociatedTokenAccount,
	outAssociatedTokenAccount,
	amount,
	swapMode,
	slippage,
	usdEstimate,
	maxAccounts,
	maxRetries,
	onlyDirectRoutes = false,
	additionalIxs = [],
	quoteToUse,
	forceJupiterClient = false,
	velocityClient,
	swapClient: initialSwapClient,
	getTxParams,
	useFeePayer = false,
}: BuildSwapTxWithRetryLogicProps): Promise<{
	tx: VersionedTransaction;
	ixs: TransactionInstruction[];
	lookupTables?: AddressLookupTableAccount[];
	inAmount: BigNum;
	outAmount: BigNum;
	success: boolean;
	/** Failure from the final attempt, so callers can classify it for the user. */
	error?: SwapAttemptError;
}> => {
	// Kept stable across retries — recreating it drops the client's lookup
	// table cache and forces every retry to refetch the same ALTs.
	const swapClient =
		forceJupiterClient || swapOutputForcesJupiter(outputMarketConfig.mint)
			? createSwapClient({
					clientType: 'jupiter',
					connection: velocityClient.connection,
				})
			: initialSwapClient;

	const slippageParams =
		slippage === 'dynamic'
			? {
					autoSlippage: true,
					maxAutoSlippageBps: 300, // 3 pct
					usdEstimate: Math.round(usdEstimate),
				}
			: {
					slippageBps: (slippage as number) * 100,
					autoSlippage: false,
				};

	// The wallet the route is built and signed for. Not `authority`, which
	// follows the selected account and names a different wallet whenever that
	// account is one delegated to this one — `getProviderSwapIx` builds with the
	// connected wallet either way, and rejects a route quoted for anyone else.
	const taker = velocityClient.wallet.publicKey;

	let maxAccountsToTry = maxAccounts ?? DEFAULT_SWAP_MAX_ACCOUNTS;
	let retries = 0;
	let simulationFailed = true;
	let lastError: SwapAttemptError | undefined;

	// Shrink the route budget for the next attempt. Bottoming out at a floor
	// Jupiter can actually route within — below it every quote comes back as an
	// error body, which then fails deserialization on the /swap endpoint.
	const reduceMaxAccounts = () => {
		maxAccountsToTry = Math.max(
			maxAccountsToTry - SWAP_MAX_ACCOUNTS_RETRY_STEP,
			MIN_SWAP_MAX_ACCOUNTS
		);
	};

	while (simulationFailed && retries <= maxRetries) {
		try {
			if (retries > 0) {
				await sleep(getRetryDelayMs(retries));
			}

			// Reuse the approved quote for the first attempt; re-quote on retries
			// since the budget changed and the old route no longer applies.
			//
			// A quote carries the route that executes it, so only the provider
			// that issued it can build from it. The form quotes with the
			// configured client, which isn't the one used here when the swap has
			// been diverted to Jupiter above.
			//
			// The size has to match too: the form debounces re-quoting, so a quote
			// for the previous amount can still be on screen when the swap fires.
			// `getProviderSwapIx` rejects those, and re-quoting is what the user
			// wanted anyway.
			const quotedAmount =
				swapMode === 'ExactOut' ? quoteToUse?.outAmount : quoteToUse?.inAmount;

			const canReuseQuote =
				retries === 0 &&
				!!quoteToUse &&
				quoteToUse.providerRoute?.provider === swapClient?.getClientType() &&
				quotedAmount === amount.toString();

			// Late retries force direct routes to shrink the tx, which v2 can't
			// quote — so the client is picked per attempt, falling back to v1
			// (`swapClient`) once the escalation kicks in.
			const directRoutesOnly = onlyDirectRoutes || retries > 2;

			// Quoting only: the route a v2 quote carries builds on any Jupiter
			// client, so `swapClient` still executes below.
			const quoteClient = getQuoteSwapClient({
				swapClient,
				connection: velocityClient.connection,
				swapMode,
				autoSlippage: slippageParams.autoSlippage,
				userPublicKey: taker,
				onlyDirectRoutes: directRoutesOnly,
			});

			const quote = canReuseQuote
				? quoteToUse
				: await quoteClient.getQuote({
						inputMint: inputMarketConfig.mint,
						outputMint: outputMarketConfig.mint,
						amount: amount,
						userPublicKey: taker,
						swapMode: swapMode,
						maxAccounts: maxAccountsToTry,
						onlyDirectRoutes: directRoutesOnly,
						...slippageParams,
					});

			const quoteError = quote?.error ?? 'missing required fields';

			if (!isValidQuote(quote)) {
				// A route/error body would deserialize-fail on the swap endpoint —
				// treat it as a normal failed attempt and shrink the budget.
				throw new Error(`Invalid swap quote: ${quoteError}`);
			}

			const outAmount = new BN(quote.outAmount);
			const inAmount = new BN(quote.inAmount);

			const swapIxs = await velocityClient?.getProviderSwapIx({
				swapProvider: swapClient,
				outMarketIndex: outputMarketConfig.marketIndex,
				inMarketIndex: inputMarketConfig.marketIndex,
				outAssociatedTokenAccount,
				inAssociatedTokenAccount,
				amount,
				slippageBps: slippageParams.slippageBps,
				swapMode: swapMode,
				onlyDirectRoutes: directRoutesOnly,
				// Kept in step with the quote above, so the budget still applies if
				// the SDK ever has to quote for itself.
				maxAccounts: maxAccountsToTry,
				reduceOnly: undefined,
				quote: quote,
				userAccountPublicKey: accountPubKey,
			});

			if (!swapIxs) {
				throw new Error('Failed to get swap instructions');
			}

			const instructionsForTx = [...additionalIxs, ...(swapIxs?.ixs ?? [])];

			const walletBeforeBuild = velocityClient.txHandler.getWallet();

			if (useFeePayer) {
				velocityClient.txHandler.updateWallet(FEE_PAYER_WALLET);
			}

			let tx: VersionedTransaction;
			try {
				tx = (await velocityClient.buildTransaction(
					instructionsForTx,
					getTxParams({
						computeUnits: MAX_COMPUTE_UNITS,
						useSimulatedComputeUnits: false,
					}),
					0,
					swapIxs?.lookupTables
				)) as VersionedTransaction;
			} finally {
				if (useFeePayer) {
					velocityClient.txHandler.updateWallet(walletBeforeBuild);
				}
			}

			// Size-check before simulating. An oversized tx fails inside web3.js
			// serialization with an opaque RangeError, and only after we've paid
			// for the round-trip — usually because a lookup table failed to fetch
			// and its accounts got inlined.
			const txSize = getSerializedTransactionSize(tx);

			if (txSize > MAX_TX_BYTE_SIZE) {
				throw new Error(
					`Transaction too large: ${txSize} bytes (max ${MAX_TX_BYTE_SIZE})`
				);
			}

			const simulation = (await velocityClient?.connection.simulateTransaction(
				tx as VersionedTransaction,
				{
					innerInstructions: true,
				} as SimulateTransactionConfig
			)) as RpcResponseAndContext<
				SimulatedTransactionResponse & {
					innerInstructions: {
						instructions: ParsedInstruction[];
					}[];
				}
			>;

			if (simulation?.value.err) {
				console.log('Swap simulation failed:', simulation.value.err);
				simulationFailed = true;
				// Keep the logs — on-chain failures (e.g. heap exhaustion) are only
				// identifiable from them.
				lastError = toSwapAttemptError(
					new Error(
						`Swap simulation failed: ${JSON.stringify(simulation.value.err)}`
					),
					simulation.value.logs ?? undefined
				);
				reduceMaxAccounts();
				retries++;
			} else {
				simulationFailed = false;
				return {
					tx,
					ixs: instructionsForTx,
					lookupTables: swapIxs?.lookupTables,
					inAmount: BigNum.from(inAmount, inputMarketConfig.precisionExp),
					outAmount: BigNum.from(outAmount, outputMarketConfig.precisionExp),
					success: true,
				};
			}
		} catch (e: any) {
			console.log('Failed to simulate:', e);
			simulationFailed = true;
			lastError = toSwapAttemptError(e, e?.logs);
			reduceMaxAccounts();
			retries++;
		}
	}

	return {
		tx: null as unknown as VersionedTransaction,
		ixs: [],
		lookupTables: undefined,
		inAmount: null as unknown as BigNum,
		outAmount: null as unknown as BigNum,
		success: false,
		error: lastError,
	};
};
